import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonicModule, AlertController, LoadingController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import moment from 'moment';

import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { getWhereClausesFromFilters } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

interface ConversationDetails {
  id: string;
  friendlyId: number;
  title: string | null;
  summary: string;
}

interface KnowledgeGapTopic {
  id: string;
  conversationId: string;
  topicTitle: string;
  topicDescription: string;
  context: string;
  confidence: number;
  createdAt: string;
  citedArticleId?: string;
  conversation: ConversationDetails;
}

interface InsightAction {
  type: 'create_new' | 'update_existing';
  description: string;
  topics: string[];
  signalType: 'citation_failure' | 'knowledge_gap';
  index: number;
  isDismissed: boolean;
}

interface KnowledgeGapInsight {
  id: string;
  tenantId: string;
  projectId: string;
  category: string;
  language: string;
  topics: KnowledgeGapTopic[];
  actions: InsightAction[];
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeGapInsightsResponse {
  insights: KnowledgeGapInsight[];
  meta: {
    total: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    SharedPipesModule,
    RouterModule,
    FilterButtonComponent,
  ],
  selector: 'app-knowledge-gap-insights',
  templateUrl: './knowledge-gap-insights.component.html',
  styleUrls: ['./knowledge-gap-insights.component.scss'],
})
export class KnowledgeGapInsightsComponent implements OnInit {
  isLoading = true;
  ColumnMode = ColumnMode;

  limit = 20;
  cursor: any;
  searchText?: string;
  insights: KnowledgeGapInsight[] = [];

  sortField = 'createdAt';
  sortDesc = true;
  sortLabel = 'Newest';

  filters: SelectedFilterMap = new Map();
  filterOptions: FilterOption[] = [
    {
      label: 'Project',
      id: 'projectId',
      icon: 'folder-open',
      type: 'select',
    },
    {
      label: 'Show dismissed actions',
      id: 'showDismissed',
      icon: 'eye-off',
      type: 'select',
      values: [
        { label: 'Yes', id: 'true' },
        { label: 'No', id: 'false' },
      ],
    },
  ];

  fetchInsights = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest = {}): Promise<FetchDataResponse<KnowledgeGapInsight>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      search: this.searchText || undefined,
    };

    const where = this.getWhereClauses();
    if (where.length) {
      options.where = where;
    }

    // Add pagination parameters
    const params: any = {
      limit,
      descending: descending ? 'true' : 'false',
    };

    // Only add cursor if it exists
    if (cursor) {
      params.cursor = cursor;
    }

    // Add filter parameters
    if (this.authService.selectedProject) {
      params.projectId = this.authService.selectedProject.id;
    }

    // Add search text
    if (this.searchText && this.searchText.trim()) {
      params.search = this.searchText.trim();
    }

    // Add showDismissed parameter
    const showDismissedFilter = this.filters.get(this.filterOptions.find((f) => f.id === 'showDismissed'));
    if (showDismissedFilter && showDismissedFilter.length > 0 && showDismissedFilter[0].id === 'true') {
      params.showDismissed = true;
    }

    // Process filters - convert from Map to backend parameters
    this.filters.forEach((values, filterOption) => {
      if (values && values.length > 0) {
        const selectedValue = values[0].id;

        switch (filterOption.id) {
          case 'projectId':
            params.projectId = selectedValue;
            break;
          case 'showDismissed':
            // This is handled separately below
            break;
        }
      }
    });

    const result = (await this.apiService.postPromise(
      `/knowledge-gaps/insights`,
      params,
    )) as KnowledgeGapInsightsResponse;

    return {
      data: result.insights,
      totalCount: result.meta.total,
      cursors: {
        hasNext: result.meta.hasNext,
        hasPrevious: result.meta.hasPrevious,
      },
    };
  };

  insightsDataSource = createDataSource(this.fetchInsights);

  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.getInsightsInitial();
  }

  async getInsightsInitial() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.firstPage();
      this.updateFilterOptions();
    } finally {
      this.isLoading = false;
    }
  }

  getWhereClauses() {
    const filterWheres = getWhereClausesFromFilters(this.filters);

    if (this.authService.selectedProject) {
      filterWheres.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    return filterWheres;
  }

  async getInsights() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.firstPage();
      this.updateFilterOptions();
    } finally {
      this.isLoading = false;
    }
  }

  updateFilterOptions() {
    // Project filter options could be populated from authService.projects if needed
    // For now, keeping it simple since users can already filter by selected project
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.getInsights();
  }

  onSearch() {
    this.getInsights();
  }

  displayDate(date: string) {
    return moment(date).fromNow();
  }

  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  async dismissAction(insight: KnowledgeGapInsight) {
    const loading = await this.loadingController.create({
      message: 'Dismissing action...',
    });
    await loading.present();

    try {
      await this.apiService.postPromise(`/knowledge-gaps/insights/${insight.id}/actions/dismiss`, {});
      await loading.dismiss();
      this.messageService.show('Action dismissed successfully.');
      // Refresh insights to get updated action status
      await this.insightsDataSource.reloadPage();
    } catch (error) {
      await loading.dismiss();
      this.messageService.showError('Failed to dismiss action: ' + (error.message || 'Unknown error'));
    }
  }

  getRelatedTopicsDisplay(topics: string[]): string {
    if (!topics || topics.length === 0) return '';
    if (topics.length <= 2) return topics.join(', ');
    return `${topics.slice(0, 2).join(', ')} +${topics.length - 2} more`;
  }

  getSynopsis(insight: KnowledgeGapInsight): string {
    const totalConversations = this.getTotalConversationCount(insight);
    return `Analysis of ${totalConversations} conversations identified patterns in ${insight.category}.`;
  }

  getConversationUrl(topic: string): string {
    // Simple implementation - navigate to conversations with a search for the topic
    const encodedTopic = encodeURIComponent(topic);
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations?search=${encodedTopic}`;
  }

  getConversationUrlByTopic(topic: KnowledgeGapTopic): string {
    // Navigate directly to the conversation using conversationId
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations/${topic.conversationId}`;
  }

  getConversationUrlForCategory(category: string): string {
    // Navigate to conversations page with search for the category
    const encodedCategory = encodeURIComponent(category);
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations?search=${encodedCategory}`;
  }

  getTotalConversationCount(insight: KnowledgeGapInsight): number {
    // Count unique conversations from topics
    if (!insight.topics) return 0;
    const uniqueConversationIds = new Set(insight.topics.map((topic) => topic.conversationId));
    return uniqueConversationIds.size;
  }

  trackByInsight(index: number, insight: KnowledgeGapInsight): string {
    return insight.id;
  }

  async nextPage() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.nextPage();
    } finally {
      this.isLoading = false;
    }
  }

  async previousPage() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.previousPage();
    } finally {
      this.isLoading = false;
    }
  }

  getConversationTitle(topic: KnowledgeGapTopic): string {
    // Use conversation title if available, otherwise fall back to topic title
    const title = topic.conversation.title || topic.topicTitle || 'Conversation';
    return `#${topic.conversation.friendlyId} - ${title}`;
  }

  getActionButtonText(action: InsightAction): string {
    switch (action.type) {
      case 'create_new':
        return 'Generate Article';
      case 'update_existing':
        return 'Update Article';
      default:
        return 'Execute Action';
    }
  }

  getActionForTopic(insight: KnowledgeGapInsight, topic: KnowledgeGapTopic): InsightAction | null {
    if (!insight.actions || insight.actions.length === 0) return null;

    // Check if "Show dismissed actions" filter is enabled
    const showDismissedFilter = this.filters.get(this.filterOptions.find((f) => f.id === 'showDismissed'));
    const showDismissedActions =
      showDismissedFilter && showDismissedFilter.length > 0 && showDismissedFilter[0].id === 'true';

    // Find action that references this topic
    if (showDismissedActions) {
      // When showing dismissed actions, return any action for this topic
      return insight.actions.find((action) => action.topics.includes(topic.id)) || null;
    } else {
      // Default behavior: only show non-dismissed actions
      return insight.actions.find((action) => !action.isDismissed && action.topics.includes(topic.id)) || null;
    }
  }

  async executeTopicAction(topic: KnowledgeGapTopic, action: InsightAction) {
    // Navigate to article editor immediately and let it handle the generation
    this.router.navigate(['../articles/new'], {
      relativeTo: this.route,
      queryParams: {
        topicId: topic.id,
        from: 'insights',
      },
    });
  }

  async dismissTopicAction(insight: KnowledgeGapInsight, topic: KnowledgeGapTopic, action: InsightAction) {
    const loading = await this.loadingController.create({
      message: 'Dismissing action...',
    });
    await loading.present();

    try {
      await this.apiService.postPromise(`/knowledge-gaps/insights/${insight.id}/actions/dismiss`, {
        actionType: action.type,
        topicId: topic.id,
      });

      await loading.dismiss();
      this.messageService.show('Action dismissed successfully.');
      // Refresh insights to get updated action status
      await this.insightsDataSource.reloadPage();
    } catch (error: any) {
      await loading.dismiss();
      this.messageService.showError(error);
    }
  }

  async undismissTopicAction(insight: KnowledgeGapInsight, topic: KnowledgeGapTopic, action: InsightAction) {
    const loading = await this.loadingController.create({
      message: 'Restoring action...',
    });
    await loading.present();

    try {
      await this.apiService.deletePromise(`/knowledge-gaps/insights/${insight.id}/actions/dismiss`, undefined, {
        actionType: action.type,
        topicId: topic.id,
      });

      await loading.dismiss();
      this.messageService.show('Action restored successfully.');
      // Refresh insights to get updated action status
      await this.insightsDataSource.reloadPage();
    } catch (error: any) {
      await loading.dismiss();
      this.messageService.showError(error);
    }
  }
}
